import { type RefObject, useEffect, useLayoutEffect } from 'react';

const DRAG_THRESHOLD = 5;

export const useStickyTop = (ref: RefObject<HTMLElement | null>) => {
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        let scroller = element.parentElement;
        while (scroller) {
            const { overflowY } = window.getComputedStyle(scroller);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                break;
            }
            scroller = scroller.parentElement;
        }
        const paddingTop = scroller ? Number.parseFloat(window.getComputedStyle(scroller).paddingTop) || 0 : 0;
        element.style.top = `${-paddingTop}px`;
    }, [ref]);
};

export const useSyncedScroll = (
    bodyRef: RefObject<HTMLDivElement | null>,
    headRef: RefObject<HTMLDivElement | null>,
    barRef: RefObject<HTMLDivElement | null>
) => {
    useEffect(() => {
        const body = bodyRef.current;
        const head = headRef.current;
        const bar = barRef.current;
        if (!body || !head || !bar) {
            return;
        }

        const fromBody = () => {
            head.scrollLeft = body.scrollLeft;
            if (Math.abs(bar.scrollLeft - body.scrollLeft) > 1) {
                bar.scrollLeft = body.scrollLeft;
            }
        };
        const fromBar = () => {
            if (Math.abs(body.scrollLeft - bar.scrollLeft) > 1) {
                body.scrollLeft = bar.scrollLeft;
            }
        };

        const drag = { active: false, moved: false, startX: 0, scrollLeft: 0, pointerId: -1 };
        const suppressClick = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const endDrag = () => {
            if (!drag.active) {
                return;
            }
            const { moved, pointerId } = drag;
            drag.active = false;
            drag.moved = false;
            body.classList.remove('mt-kb__body--dragging');
            if (moved) {
                document.addEventListener('click', suppressClick, { capture: true, once: true });
                window.setTimeout(() => document.removeEventListener('click', suppressClick, { capture: true }), 100);
            }
            if (body.hasPointerCapture(pointerId)) {
                body.releasePointerCapture(pointerId);
            }
        };
        const onPointerDown = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) {
                return;
            }
            Object.assign(drag, {
                active: true,
                moved: false,
                startX: event.clientX,
                scrollLeft: body.scrollLeft,
                pointerId: event.pointerId,
            });
        };
        const onPointerMove = (event: PointerEvent) => {
            if (!drag.active || event.pointerId !== drag.pointerId) {
                return;
            }
            if (event.buttons === 0) {
                endDrag();
                return;
            }
            const walk = event.clientX - drag.startX;
            if (!drag.moved && Math.abs(walk) <= DRAG_THRESHOLD) {
                return;
            }
            if (!drag.moved) {
                drag.moved = true;
                body.setPointerCapture(drag.pointerId);
                body.classList.add('mt-kb__body--dragging');
            }
            event.preventDefault();
            body.scrollLeft = drag.scrollLeft - walk;
        };
        const preventNativeDrag = (event: DragEvent) => event.preventDefault();

        body.addEventListener('scroll', fromBody, { passive: true });
        bar.addEventListener('scroll', fromBar, { passive: true });
        body.addEventListener('pointerdown', onPointerDown);
        body.addEventListener('dragstart', preventNativeDrag);
        body.addEventListener('lostpointercapture', endDrag);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);
        window.addEventListener('blur', endDrag);
        fromBody();

        return () => {
            endDrag();
            body.removeEventListener('scroll', fromBody);
            bar.removeEventListener('scroll', fromBar);
            body.removeEventListener('pointerdown', onPointerDown);
            body.removeEventListener('dragstart', preventNativeDrag);
            body.removeEventListener('lostpointercapture', endDrag);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endDrag);
            window.removeEventListener('pointercancel', endDrag);
            window.removeEventListener('blur', endDrag);
            document.removeEventListener('click', suppressClick, { capture: true });
        };
    }, [bodyRef, headRef, barRef]);
};
